import { Topic } from "@/types";
import {
  topicCoverUrl,
  topicToAlbumSummary,
  topicToGallerySummary,
} from "@/features/album/mappers";
import {
  AlbumSummary,
  GallerySummary,
  TopicOwner,
} from "@/features/album/types";

/**
 * The one thing every Discover card needs that `/albums`/`/galleries` never
 * did: whose content this is. `userName`/`profilePicThumbURL` are already on
 * `Topic` and already rendered by legacy `DiscoverHome.tsx` — just not
 * carried through `topicToAlbumSummary`/`topicToGallerySummary`, since those
 * mappers only ever served "my stuff" grids where the viewer is the owner.
 */
function ownerFromTopic(topic: Topic): TopicOwner | undefined {
  if (!topic.userName) return undefined;
  return {
    name: topic.userName,
    avatarUrl: topic.profilePicThumbURL || undefined,
  };
}

function likesFromTopic(topic: Topic): number {
  return Number(topic.totalLikes) || 0;
}

/** Discover-flavored album mapping — same shape as `/search`, plus owner + likes. */
export function topicToDiscoverAlbum(topic: Topic): AlbumSummary {
  return {
    ...topicToAlbumSummary(topic),
    owner: ownerFromTopic(topic),
    likes: likesFromTopic(topic),
  };
}

/**
 * Discover-flavored gallery mapping — same shape as `/search`, plus owner +
 * likes, plus a stand-in hung print.
 *
 * `topicToGallerySummary` always returns `arts: []` — real hung-print URLs
 * are never resolved this way, only via the room's own scene note (see
 * `GalleryCard`'s `sceneLoaded`/`arts` comment). The one cheap stand-in
 * available here: the *source album's* cover photo, same trick
 * `GalleryCard`/`GalleryListRow` use via their `sourceAlbumCoverUrl` prop —
 * except those get it from the "my albums" list the owner's shell already
 * has loaded, and Discover has no equivalent for someone else's rooms unless
 * that source album also happens to be in this page's own public-albums
 * fetch. Pass it in as `sourceAlbumTopic` when available (see
 * `useDiscoverFeed`); `topicToAlbumSummary(topic).coverUrl` below is a second,
 * near-always-empty fallback kept only because a gallery topic can in theory
 * carry its own `photos`/`notes`.
 */
export function topicToDiscoverGallery(
  topic: Topic,
  sourceAlbumTopic?: Topic,
): GallerySummary {
  const base = topicToGallerySummary(topic);
  const fallbackCover =
    (sourceAlbumTopic && topicCoverUrl(sourceAlbumTopic)) ||
    topicToAlbumSummary(topic).coverUrl;

  return {
    ...base,
    arts:
      base.arts.length > 0
        ? base.arts
        : fallbackCover
          ? [{ img: fallbackCover }]
          : [],
    owner: ownerFromTopic(topic),
    likes: likesFromTopic(topic),
  };
}
